import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { ReactFlowProvider } from '@xyflow/react';
import { FunnelBuilderProvider, useFunnelBuilder } from '../contexts/FunnelBuilderContext';
import { useApp } from '../contexts/AppContext';
import {
    FunnelCanvas,
    FunnelToolbar,
    NodePanel,
    PropertiesPanel,
    CreativePickerModal
} from '../components/funnel-builder';
import {
    GuidanceHub,
    FunnelAIAssistant,
    ContextualTips
} from '../components/funnel-builder/guidance';
import { autoLayoutNodes } from '../utils/funnelHelpers';

import '@xyflow/react/dist/style.css';

/**
 * FunnelBuilderContent Component
 * Inner component that uses the FunnelBuilder context
 */
const FunnelBuilderContent = () => {
    const {
        nodes,
        edges,
        viewport,
        setViewport,
        addNode,
        deleteNode,
        selectedNode,
        setSelectedNode,
        onNodesChange,
        onEdgesChange,
        onConnect,
        onNodeClick,
        onPaneClick,
        modalState,
        openCreativePicker,
        closeCreativePicker,
        activePlatform,
        setActivePlatform,
        platforms,
        loadFunnel,
        applyTemplate,
        isLoading,
        // Platform connections
        currentPlatformConnection,
        initiateOAuthConnection,
        // Properties Panel
        updateSelectedNodeData
    } = useFunnelBuilder();

    const { selectedBrand, user } = useApp();

    const [searchParams] = useSearchParams();
    const location = useLocation();
    const funnelId = searchParams.get('id');

    const reactFlowWrapper = useRef(null);
    const [reactFlowInstance, setReactFlowInstance] = React.useState(null);

    // Guidance system state
    const [showGuidanceHub, setShowGuidanceHub] = useState(false);
    const [showAIAssistant, setShowAIAssistant] = useState(false);

    // Load funnel from URL param
    useEffect(() => {
        if (funnelId) {
            loadFunnel(funnelId).catch(err => {
                console.error('Failed to load funnel:', err);
            });
        }
    }, [funnelId, loadFunnel]);

    // Handle incoming template from Deploy to Funnel (Bulk Content Planner)
    const hasAppliedTemplate = useRef(false);
    useEffect(() => {
        if (location.state?.generatedTemplate && !hasAppliedTemplate.current) {
            hasAppliedTemplate.current = true;
            const { generatedTemplate, platform } = location.state;

            // Switch to the target platform if specified
            if (platform && platforms.includes(platform)) {
                setActivePlatform(platform);
            }

            // Apply the template (creates nodes + edges with auto-layout)
            // Pillar data (pillarId, pillarName, pillarColor) is already embedded
            // in the campaign node data by buildFunnelTemplate()
            applyTemplate(generatedTemplate);

            // Clear navigation state to prevent re-applying on re-render
            window.history.replaceState({}, document.title);

            console.log('[FunnelBuilder] Applied template from Bulk Content Planner');
        }
    }, [location.state, applyTemplate, platforms, setActivePlatform]);

    // Handle auto-layout
    const handleAutoLayout = useCallback(() => {
        const layoutedNodes = autoLayoutNodes(nodes, edges);
        // Use onNodesChange with position changes to update node positions
        const positionChanges = layoutedNodes.map(node => ({
            id: node.id,
            type: 'position',
            position: node.position,
            dragging: false
        }));
        onNodesChange(positionChanges);
    }, [nodes, edges, onNodesChange]);

    // Handle adding node from panel
    const handleAddNode = useCallback((type) => {
        const funnelStage = type === 'campaign' ? 'TOFU' :
                          type === 'adset' ? 'MOFU' : 'BOFU';
        addNode(type, { funnelStage });
    }, [addNode]);

    // Handle drag and drop from panel
    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDrop = useCallback((event) => {
        event.preventDefault();

        const type = event.dataTransfer.getData('application/reactflow');
        if (!type || !reactFlowInstance) return;

        // Calculate drop position in flow coordinates
        const position = reactFlowInstance.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        });

        // Determine funnel stage based on x position
        let funnelStage = 'TOFU';
        if (position.x > 600) {
            funnelStage = 'BOFU';
        } else if (position.x > 300) {
            funnelStage = 'MOFU';
        }

        addNode(type, { funnelStage }, null);
    }, [reactFlowInstance, addNode]);

    // Handle viewport change
    const handleMoveEnd = useCallback((event, newViewport) => {
        setViewport(newViewport);
    }, [setViewport]);

    // Handle wizard completion - create nodes from wizard data
    const handleWizardComplete = useCallback((data) => {
        // Add campaign
        const campaignId = addNode('campaign', {
            name: data.campaign.name,
            objective: data.campaign.objective,
            budget: data.campaign.budget,
            funnelStage: data.campaign.funnelStage,
            status: 'draft'
        });

        // Add ad set connected to campaign
        setTimeout(() => {
            const adsetId = addNode('adset', {
                name: data.adset.name,
                targeting: data.adset.targeting,
                funnelStage: data.campaign.funnelStage,
                status: 'draft'
            }, campaignId);

            // Add ad connected to ad set
            setTimeout(() => {
                addNode('ad', {
                    name: data.ad.name,
                    headline: data.ad.headline,
                    primaryText: data.ad.primaryText,
                    cta: data.ad.cta,
                    funnelStage: data.campaign.funnelStage,
                    status: 'draft'
                }, adsetId);

                // Add retargeting pool if configured
                if (data.retargetingPool) {
                    setTimeout(() => {
                        addNode('retargetingPool', {
                            name: data.retargetingPool.name,
                            poolType: data.retargetingPool.poolType,
                            sourceStage: data.retargetingPool.sourceStage,
                            targetStage: data.retargetingPool.targetStage,
                            trigger: data.retargetingPool.trigger,
                            estimatedSize: data.retargetingPool.estimatedSize,
                            isActive: data.retargetingPool.isActive,
                            status: 'draft'
                        });
                    }, 100);
                }
            }, 100);
        }, 100);
    }, [addNode]);

    // Platform tab colors
    const platformColors = {
        meta: '#1877F2',
        google: '#4285F4',
        tiktok: '#000000',
        linkedin: '#0A66C2'
    };

    const platformLabels = {
        meta: 'Meta',
        google: 'Google',
        tiktok: 'TikTok',
        linkedin: 'LinkedIn'
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 64px)',
            marginLeft: '-32px',
            marginRight: '-32px',
            marginTop: '-32px',
            marginBottom: '-32px'
        }}>
            {/* Platform Tabs */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--color-surface)',
                borderBottom: '1px solid var(--color-border)'
            }}>
                {platforms.map(platform => (
                    <button
                        key={platform}
                        onClick={() => setActivePlatform(platform)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-2) var(--space-4)',
                            background: activePlatform === platform
                                ? 'var(--color-bg-tertiary)'
                                : 'transparent',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            fontWeight: activePlatform === platform ? 600 : 400,
                            color: activePlatform === platform
                                ? 'var(--color-text-primary)'
                                : 'var(--color-text-secondary)',
                            transition: 'all 0.15s ease'
                        }}
                    >
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: platformColors[platform]
                        }} />
                        {platformLabels[platform]}
                    </button>
                ))}
            </div>

            {/* Toolbar */}
            <FunnelToolbar
                onAutoLayout={handleAutoLayout}
                onOpenHelp={() => setShowGuidanceHub(true)}
            />

            {/* Main Content */}
            <div style={{
                flex: 1,
                display: 'flex',
                overflow: 'hidden'
            }}>
                {/* Node Panel */}
                <NodePanel onAddNode={handleAddNode} />

                {/* Canvas */}
                <div
                    ref={reactFlowWrapper}
                    style={{ flex: 1 }}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <FunnelCanvas
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        onPaneClick={onPaneClick}
                        onMoveEnd={handleMoveEnd}
                        onInit={setReactFlowInstance}
                        defaultViewport={viewport}
                    />
                </div>

                {/* Properties Panel (or Funnel Summary when nothing selected) */}
                <PropertiesPanel
                    platformConnection={currentPlatformConnection}
                    onConnectPlatform={() => initiateOAuthConnection(activePlatform)}
                    onOpenCreativePicker={(callback) => openCreativePicker(callback)}
                    onGenerateCreative={() => {
                        // Navigate to creative generation or open generator modal
                        console.log('Generate creative clicked');
                    }}
                    brandContext={{ ...selectedBrand, userId: user?.uid }}
                />
            </div>

            {/* Creative Picker Modal - still needed for selecting assets */}
            <CreativePickerModal
                isOpen={modalState.creativePicker.isOpen}
                onClose={closeCreativePicker}
                onSelect={(creative) => {
                    if (modalState.creativePicker.callback) {
                        modalState.creativePicker.callback(creative);
                    }
                    closeCreativePicker();
                }}
            />

            {/* Guidance System */}
            <GuidanceHub
                isOpen={showGuidanceHub}
                onClose={() => setShowGuidanceHub(false)}
                onApplyTemplate={applyTemplate}
                onWizardComplete={handleWizardComplete}
                nodes={nodes}
                edges={edges}
            />

            <FunnelAIAssistant
                nodes={nodes}
                edges={edges}
                isOpen={showAIAssistant}
                onToggle={() => setShowAIAssistant(!showAIAssistant)}
            />

            <ContextualTips
                nodes={nodes}
                edges={edges}
                onAddNode={handleAddNode}
                onEditNode={(node) => openModal(node.type, 'edit', { id: node.id, ...node.data })}
            />
        </div>
    );
};

/**
 * FunnelBuilder Page
 * Main page component with providers
 */
const FunnelBuilder = () => {
    return (
        <ReactFlowProvider>
            <FunnelBuilderProvider>
                <FunnelBuilderContent />
            </FunnelBuilderProvider>
        </ReactFlowProvider>
    );
};

export default FunnelBuilder;
